// ============================================================
// Swiss Finance OS - Booking Service
// Handles transaction booking with Bezugsteuer support
// Phase 1 & 2: Manual Review + Reverse Charge VAT
// ============================================================

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

export interface BookingData {
  transactionId: string;
  debitAccount: string;
  creditAccount: string;
  amount: number;
  vatRate: number;
  description: string;
  date: string;
}

export interface BezugsteuerCheck {
  required: boolean;
  reason: string;
  vatCode: string;
  estv_ziffer: string;
}

/**
 * Check if transaction requires Bezugsteuer (reverse charge VAT)
 * Art. 45 MWSTG - B2B services from abroad
 */
export const checkBezugsteuer = (transaction: any): BezugsteuerCheck => {
  const counterparty = (transaction.counterparty?.toLowerCase() || '');
  const description = (transaction.description?.toLowerCase() || '');
  const aiClassification = transaction.ai_classification;
  
  // Foreign service providers that commonly require Bezugsteuer
  const foreignServiceIndicators = [
    'google', 'aws', 'amazon web services', 'azure', 'microsoft', 'meta', 'facebook',
    'stripe', 'paypal', 'shopify', 'linkedin', 'zoom', 'cloudflare',
    'github', 'gitlab', 'atlassian', 'slack', 'dropbox', 'adobe',
    'openai', 'anthropic', 'hubspot', 'mailchimp', 'sendgrid'
  ];
  
  const isForeignService = foreignServiceIndicators.some(indicator => 
    counterparty.includes(indicator) || description.includes(indicator)
  );
  
  // IT/Digital services accounts that commonly have foreign providers
  const itServiceAccounts = ['6570', '6700', '6800']; // IT services, Marketing, Admin expenses
  const isITServiceAccount = itServiceAccounts.includes(aiClassification?.accountNumber);
  
  if (isForeignService && isITServiceAccount) {
    return {
      required: true,
      reason: 'B2B Dienstleistung aus dem Ausland (Art. 45 MWSTG)',
      vatCode: 'RC-STD',
      estv_ziffer: '382' // Bezugsteuer auf Dienstleistungen
    };
  }
  
  return { required: false, reason: '', vatCode: '', estv_ziffer: '' };
};

/**
 * Create Swiss GAAP booking from transaction with Bezugsteuer support
 */
export const createBookingFromTransaction = async (transactionId: string): Promise<{ entry_number: string }> => {
  // 1) Fetch transaction details
  const { data: txn, error: txnError } = await supabase
    .from('transactions')
    .select('*')
    .eq('id', transactionId)
    .single();

  if (txnError || !txn) {
    logger.error('Failed to fetch transaction for booking', { transactionId, error: txnError }, 'booking');
    throw new Error(txnError?.message || 'Transaction not found');
  }

  // Check if already booked
  if (txn.journal_entry_id) {
    throw new Error('Transaction bereits gebucht');
  }

  // Check if approved for booking
  if (!txn.booking_approved) {
    throw new Error('Transaction muss zuerst genehmigt werden');
  }

  // Check Bezugsteuer requirement
  const bezugsteuerCheck = checkBezugsteuer(txn);
  
  // Extract VAT data from AI classification
  const aiClass = (txn.ai_classification as any) || {};
  const vatRate = aiClass?.vatRate || 0;
  const grossAmount = Math.abs(txn.amount);
  
  let netAmount: number;
  let vatAmount: number;
  
  if (bezugsteuerCheck.required) {
    // Bezugsteuer: Amount is NET (foreign invoice), calculate VAT
    netAmount = grossAmount;
    vatAmount = (netAmount * vatRate) / 100;
  } else {
    // Normal VAT: Amount includes VAT
    vatAmount = grossAmount > 0 ? (grossAmount * vatRate) / (100 + vatRate) : 0;
    netAmount = grossAmount - vatAmount;
  }

  // 2) Create journal entry
  const { data: entry, error: entryError } = await supabase
    .from('journal_entries')
    .insert({
      mandate_id: txn.mandate_id,
      organization_id: txn.organization_id,
      booking_date: txn.transaction_date,
      value_date: txn.transaction_date,
      description: txn.description || txn.counterparty || 'Buchung',
      reference_number: txn.reference || '',
      status: 'posted',
      posted_at: new Date().toISOString(),
      metadata: {
        source: 'transaction_booking',
        transaction_id: txn.id,
        bezugsteuer: bezugsteuerCheck.required,
        vat_mechanism: bezugsteuerCheck.required ? 'reverse_charge' : 'standard'
      }
    })
    .select()
    .single();

  if (entryError || !entry) {
    logger.error('Failed to create journal entry', { transactionId, error: entryError }, 'booking');
    throw new Error(entryError?.message || 'Failed to create journal entry');
  }

  // 3) Create booking entries
  const bookingEntries: any[] = [];
  
  if (bezugsteuerCheck.required) {
    // === BEZUGSTEUER BOOKING (4 entries) ===
    
    // Entry 1: Expense account (NET amount) - Debit
    bookingEntries.push({
      journal_entry_id: entry.id,
      organization_id: txn.organization_id,
      mandate_id: txn.mandate_id,
      line_number: 1,
      account_number: (aiClass as any)?.accountNumber || '6570',
      account_name: (aiClass as any)?.accountName || 'IT-Dienstleistungen',
      debit_amount: netAmount,
      credit_amount: 0,
      description: `${txn.description} (netto)`,
      vat_rate: 0,
      vat_amount: 0,
      net_amount: netAmount,
      currency: txn.currency || 'CHF',
      exchange_rate: 1.0
    });
    
    // Entry 2: Input VAT (Vorsteuer) - Debit (Art. 28 MWSTG)
    bookingEntries.push({
      journal_entry_id: entry.id,
      organization_id: txn.organization_id,
      mandate_id: txn.mandate_id,
      line_number: 2,
      account_number: '2201', // Vorsteuer
      account_name: 'Vorsteuer Material und Dienstleistungen',
      debit_amount: vatAmount,
      credit_amount: 0,
      description: `Bezugsteuer ${vatRate}% auf ${txn.counterparty}`,
      vat_rate: vatRate,
      vat_amount: vatAmount,
      vat_code: bezugsteuerCheck.vatCode,
      currency: txn.currency || 'CHF',
      exchange_rate: 1.0,
      metadata: {
        bezugsteuer: true,
        estv_ziffer: '400', // Vorsteuer
        mechanism: 'reverse_charge_input'
      }
    });
    
    // Entry 3: Output VAT (Umsatzsteuer) - Credit (Art. 45 MWSTG)
    bookingEntries.push({
      journal_entry_id: entry.id,
      organization_id: txn.organization_id,
      mandate_id: txn.mandate_id,
      line_number: 3,
      account_number: '2200', // Umsatzsteuer
      account_name: 'Geschuldete Mehrwertsteuer',
      debit_amount: 0,
      credit_amount: vatAmount,
      description: `Bezugsteuer ${vatRate}% auf ${txn.counterparty}`,
      vat_rate: vatRate,
      vat_amount: vatAmount,
      vat_code: bezugsteuerCheck.vatCode,
      currency: txn.currency || 'CHF',
      exchange_rate: 1.0,
      metadata: {
        bezugsteuer: true,
        estv_ziffer: '382', // Bezugsteuer
        mechanism: 'reverse_charge_output'
      }
    });
    
    // Entry 4: Bank/Cash account - Credit
    bookingEntries.push({
      journal_entry_id: entry.id,
      organization_id: txn.organization_id,
      mandate_id: txn.mandate_id,
      line_number: 4,
      account_number: txn.debit_account || '1020',
      account_name: 'Bank',
      debit_amount: 0,
      credit_amount: netAmount,
      description: txn.description,
      currency: txn.currency || 'CHF',
      exchange_rate: 1.0
    });
    
  } else {
    // === STANDARD VAT BOOKING (2-3 entries) ===
    
    const isExpense = txn.amount < 0;
    
    // Entry 1: Main account (expense or revenue)
    bookingEntries.push({
      journal_entry_id: entry.id,
      organization_id: txn.organization_id,
      mandate_id: txn.mandate_id,
      line_number: 1,
      account_number: (aiClass as any)?.accountNumber || (isExpense ? '6000' : '3200'),
      account_name: (aiClass as any)?.accountName || (isExpense ? 'Aufwand' : 'Erlös'),
      debit_amount: isExpense ? netAmount : 0,
      credit_amount: isExpense ? 0 : netAmount,
      description: txn.description,
      vat_rate: vatRate,
      vat_amount: vatAmount,
      net_amount: netAmount,
      currency: txn.currency || 'CHF',
      exchange_rate: 1.0
    });
    
    // Entry 2: VAT account (if applicable)
    if (vatAmount > 0) {
      bookingEntries.push({
        journal_entry_id: entry.id,
        organization_id: txn.organization_id,
        mandate_id: txn.mandate_id,
        line_number: 2,
        account_number: isExpense ? '2201' : '2200', // Vorsteuer or Umsatzsteuer
        account_name: isExpense ? 'Vorsteuer Material und Dienstleistungen' : 'Geschuldete Mehrwertsteuer',
        debit_amount: isExpense ? vatAmount : 0,
        credit_amount: isExpense ? 0 : vatAmount,
        description: `MwSt ${vatRate}%`,
        vat_rate: vatRate,
        vat_amount: vatAmount,
        currency: txn.currency || 'CHF',
        exchange_rate: 1.0,
        metadata: {
          estv_ziffer: isExpense ? '400' : (vatRate === 8.1 ? '302' : vatRate === 2.6 ? '312' : '342')
        }
      });
    }
    
    // Entry 3: Bank/Cash account (contra)
    bookingEntries.push({
      journal_entry_id: entry.id,
      organization_id: txn.organization_id,
      mandate_id: txn.mandate_id,
      line_number: vatAmount > 0 ? 3 : 2,
      account_number: txn.debit_account || '1020',
      account_name: 'Bank',
      debit_amount: isExpense ? 0 : grossAmount,
      credit_amount: isExpense ? grossAmount : 0,
      description: txn.description,
      currency: txn.currency || 'CHF',
      exchange_rate: 1.0
    });
  }

  // 4) Insert all booking entries
  const { error: entriesError } = await supabase
    .from('booking_entries')
    .insert(bookingEntries);

  if (entriesError) {
    logger.error('Failed to create booking entries', { transactionId, error: entriesError }, 'booking');
    // Rollback journal entry
    await supabase.from('journal_entries').delete().eq('id', entry.id);
    throw new Error(entriesError.message || 'Failed to create booking entries');
  }

  // 5) Update transaction status
  const { error: updateError } = await supabase
    .from('transactions')
    .update({ 
      booking_status: 'booked',
      journal_entry_id: entry.id
    } as any)
    .eq('id', transactionId);

  if (updateError) {
    logger.error('Failed to update transaction status', { transactionId, error: updateError }, 'booking');
  }

  // 6) Log audit trail
  await logBookingAction({
    organization_id: txn.organization_id,
    mandate_id: txn.mandate_id,
    transaction_id: txn.id,
    journal_entry_id: entry.id,
    action: 'booking_created',
    details: {
      bezugsteuer: bezugsteuerCheck.required,
      net_amount: netAmount,
      vat_amount: vatAmount,
      entries_count: bookingEntries.length
    }
  });

  return { entry_number: entry.id };
};

/**
 * Log booking action to audit trail
 */
const logBookingAction = async (data: {
  organization_id: string;
  mandate_id: string;
  transaction_id?: string;
  journal_entry_id?: string;
  action: string;
  details?: any;
}) => {
  try {
    await supabase.from('booking_audit_log').insert({
      ...data,
      performed_by: (await supabase.auth.getUser()).data.user?.id,
      performed_at: new Date().toISOString()
    });
  } catch (error) {
    logger.error('Failed to log audit action', { error }, 'audit');
  }
};

/**
 * Approve transaction for booking
 */
export const approveTransactionForBooking = async (transactionId: string): Promise<void> => {
  const { data: txn } = await supabase
    .from('transactions')
    .select('*')
    .eq('id', transactionId)
    .single();

  if (!txn) throw new Error('Transaction not found');

  // Check Bezugsteuer requirement
  const bezugsteuerCheck = checkBezugsteuer(txn);

  const { error } = await supabase
    .from('transactions')
    .update({
      review_status: 'approved',
      booking_approved: true,
      requires_bezugsteuer: bezugsteuerCheck.required,
      reviewed_by: (await supabase.auth.getUser()).data.user?.id,
      reviewed_at: new Date().toISOString()
    })
    .eq('id', transactionId);

  if (error) throw error;

  await logBookingAction({
    organization_id: txn.organization_id,
    mandate_id: txn.mandate_id,
    transaction_id: txn.id,
    action: 'review_approved',
    details: { requires_bezugsteuer: bezugsteuerCheck.required }
  });
};

/**
 * Reject transaction
 */
export const rejectTransaction = async (transactionId: string, reason?: string): Promise<void> => {
  const { data: txn } = await supabase
    .from('transactions')
    .select('organization_id, mandate_id')
    .eq('id', transactionId)
    .single();

  if (!txn) throw new Error('Transaction not found');

  const { error } = await supabase
    .from('transactions')
    .update({
      review_status: 'rejected',
      booking_approved: false,
      reviewed_by: (await supabase.auth.getUser()).data.user?.id,
      reviewed_at: new Date().toISOString()
    })
    .eq('id', transactionId);

  if (error) throw error;

  await logBookingAction({
    organization_id: txn.organization_id,
    mandate_id: txn.mandate_id,
    transaction_id: transactionId,
    action: 'review_rejected',
    details: { reason }
  });
};

// ============= BATCH OPERATIONS =============

/**
 * Batch approve multiple transactions for booking
 */
export const batchApproveTransactions = async (transactionIds: string[]): Promise<{
  success: number;
  failed: number;
  errors: Array<{ transactionId: string; error: string }>;
}> => {
  const results = {
    success: 0,
    failed: 0,
    errors: [] as Array<{ transactionId: string; error: string }>,
  };

  for (const txnId of transactionIds) {
    try {
      await approveTransactionForBooking(txnId);
      results.success++;
    } catch (error) {
      results.failed++;
      results.errors.push({
        transactionId: txnId,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }

  return results;
};

/**
 * Batch reject multiple transactions
 */
export const batchRejectTransactions = async (transactionIds: string[], reason?: string): Promise<{
  success: number;
  failed: number;
  errors: Array<{ transactionId: string; error: string }>;
}> => {
  const results = {
    success: 0,
    failed: 0,
    errors: [] as Array<{ transactionId: string; error: string }>,
  };

  for (const txnId of transactionIds) {
    try {
      await rejectTransaction(txnId, reason);
      results.success++;
    } catch (error) {
      results.failed++;
      results.errors.push({
        transactionId: txnId,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }

  return results;
};

/**
 * Batch book multiple approved transactions
 */
export const batchBookTransactions = async (
  transactionIds: string[],
  options: {
    validateBalance?: boolean;
    dryRun?: boolean;
  } = {}
): Promise<{
  success: number;
  failed: number;
  errors: Array<{ transactionId: string; error: string }>;
  journalEntries: string[];
}> => {
  const results = {
    success: 0,
    failed: 0,
    errors: [] as Array<{ transactionId: string; error: string }>,
    journalEntries: [] as string[],
  };

  for (const txnId of transactionIds) {
    try {
      // Validate transaction is approved
      const { data: txn, error: fetchError } = await supabase
        .from('transactions')
        .select('booking_approved, journal_entry_id')
        .eq('id', txnId)
        .single();

      if (fetchError) throw fetchError;

      if (txn.journal_entry_id) {
        results.errors.push({
          transactionId: txnId,
          error: 'Already booked',
        });
        results.failed++;
        continue;
      }

      if (!txn.booking_approved) {
        results.errors.push({
          transactionId: txnId,
          error: 'Not approved for booking',
        });
        results.failed++;
        continue;
      }

      if (options.dryRun) {
        console.log(`[DRY RUN] Would book transaction ${txnId}`);
        results.success++;
      } else {
        const result = await createBookingFromTransaction(txnId);
        results.journalEntries.push(result.entry_number);
        results.success++;
      }
    } catch (error) {
      results.failed++;
      results.errors.push({
        transactionId: txnId,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }

  return results;
};

export const updateTransactionStatus = async (transactionId: string, status: 'pending' | 'review' | 'booked' | 'rejected'): Promise<void> => {
  await new Promise(resolve => setTimeout(resolve, 200));
  console.log(`Updating transaction ${transactionId} status to ${status}`);
  return Promise.resolve();
};

export const createLearningRuleFromCorrection = async (transactionId: string, correction: any): Promise<void> => {
  await new Promise(resolve => setTimeout(resolve, 300));
  console.log(`Creating learning rule from correction for transaction ${transactionId}`);
  return Promise.resolve();
};

export const getJournalEntries = async (mandateId: string, page: number = 1): Promise<any> => {
  const pageSize = 20;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  const { data: entries, error } = await supabase
    .from('journal_entries')
    .select('*')
    .eq('mandate_id', mandateId)
    .order('created_at', { ascending: false })
    .range(from, to);

  if (error) throw new Error(error.message);

  const { count, error: countError } = await supabase
    .from('journal_entries')
    .select('*', { count: 'exact', head: true })
    .eq('mandate_id', mandateId);

  if (countError) throw new Error(countError.message);

  return {
    entries: entries || [],
    totalPages: Math.ceil((count || 0) / pageSize),
    currentPage: page,
    totalCount: count || 0,
  };
};

export const bulkCreateBookings = async (transactionIds: string[]): Promise<{ success: number; total: number; errors: any[] }> => {
  const results = {
    success: 0,
    total: transactionIds.length,
    errors: [] as any[]
  };

  for (const txnId of transactionIds) {
    try {
      await createBookingFromTransaction(txnId);
      results.success++;
    } catch (error: any) {
      results.errors.push({
        transactionId: txnId,
        error: error.message
      });
    }
  }

  return results;
};

export const getVatSummary = async (mandateId: string): Promise<any> => {
  await new Promise(resolve => setTimeout(resolve, 300));
  console.log(`Fetching VAT summary for mandate ${mandateId}`);
  
  return {
    totalInput: 0,
    totalOutput: 0,
    netVat: 0
  };
};
